/**
 * Lifetime Redemption Page — Phase 2.
 *
 * Hidden URL `/lifetime`. Reached by users who already bought a code on
 * AppSumo / DealMirror / etc. Not linked from anywhere in the marketing
 * site or app navbar.
 *
 * On success (marketplace hardening):
 *   - Tenant + owner created in `pending_email_verification` state
 *   - Verification email sent (24h expiry)
 *   - User must click the email link before login is allowed
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Sparkles, Ticket, MailCheck } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'New Zealand',
  'Ireland', 'Germany', 'France', 'Netherlands', 'Spain', 'Italy', 'Portugal',
  'Sweden', 'Norway', 'Denmark', 'Finland', 'Belgium', 'Switzerland', 'Austria',
  'Mexico', 'Brazil', 'Argentina', 'Chile', 'Colombia',
  'India', 'Singapore', 'Hong Kong', 'Japan', 'South Korea', 'Philippines',
  'United Arab Emirates', 'Saudi Arabia', 'South Africa',
  'Other',
];

export default function LifetimeRedeem() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    code: '',
    business_name: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    country: 'United States',
  });
  const [submitting, setSubmitting] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(null);

  const update = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/lifetime/redeem`, {
        ...form,
        code: form.code.trim().toUpperCase(),
        email: form.email.trim().toLowerCase(),
      });
      // Marketplace hardening: no auto-login. Backend has emailed a 24h link.
      setPendingVerification({
        email: data.email || form.email,
        plan: data.plan,
        marketplace: data.marketplace,
      });
      toast.success('Check your inbox to activate your Lifetime license');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Redemption failed. Check your code and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (pendingVerification) {
    return <PendingVerificationView email={pendingVerification.email} onBack={() => navigate('/login')} />;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-br from-slate-50 to-orange-50/40"
      data-testid="lifetime-redeem-page"
    >
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
          {/* ReStaff brand mark — reused from Layout.js (same RS tile + wordmark) */}
          <div
            className="flex items-center justify-center gap-2 mb-5"
            data-testid="lifetime-brand"
          >
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">RS</span>
            </div>
            <span
              className="font-bold text-lg text-slate-900"
              style={{ fontFamily: 'Manrope' }}
            >
              ReStaff
            </span>
          </div>

          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
              <Sparkles size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Activate Your Lifetime License
              </h1>
              <p className="text-xs text-slate-500">
                Redeem the code from your marketplace purchase
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 mt-5">
            <div>
              <Label htmlFor="lt-code" className="flex items-center gap-1.5">
                <Ticket size={14} className="text-orange-500" />
                License Code
              </Label>
              <Input
                id="lt-code"
                required
                placeholder="RS-XXX-XX-XXXX"
                value={form.code}
                onChange={update('code')}
                className="font-mono uppercase"
                autoComplete="off"
                spellCheck={false}
                data-testid="lt-redeem-code"
              />
            </div>

            <div>
              <Label htmlFor="lt-biz">Business Name</Label>
              <Input
                id="lt-biz"
                required
                placeholder="Your company"
                value={form.business_name}
                onChange={update('business_name')}
                data-testid="lt-redeem-business"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="lt-first">First Name</Label>
                <Input
                  id="lt-first"
                  required
                  value={form.first_name}
                  onChange={update('first_name')}
                  data-testid="lt-redeem-first"
                />
              </div>
              <div>
                <Label htmlFor="lt-last">Last Name</Label>
                <Input
                  id="lt-last"
                  required
                  value={form.last_name}
                  onChange={update('last_name')}
                  data-testid="lt-redeem-last"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="lt-email">Email</Label>
              <Input
                id="lt-email"
                type="email"
                required
                value={form.email}
                onChange={update('email')}
                autoComplete="email"
                data-testid="lt-redeem-email"
              />
            </div>

            <div>
              <Label htmlFor="lt-pwd">Password <span className="text-slate-400 font-normal">(min 8 chars)</span></Label>
              <Input
                id="lt-pwd"
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={update('password')}
                autoComplete="new-password"
                data-testid="lt-redeem-password"
              />
            </div>

            <div>
              <Label htmlFor="lt-country">Country</Label>
              <select
                id="lt-country"
                required
                value={form.country}
                onChange={update('country')}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="lt-redeem-country"
              >
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white min-h-[44px] mt-2"
              data-testid="lt-redeem-submit"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles size={14} className="mr-1.5" />
              )}
              Activate Lifetime License
            </Button>

            <p className="text-[11px] text-slate-400 text-center pt-1">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-orange-600 hover:text-orange-700 underline"
              >
                Sign in instead
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}


function PendingVerificationView({ email, onBack }) {
  const [busy, setBusy] = React.useState(false);
  const [cooldownUntil, setCooldownUntil] = React.useState(0);
  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    if (cooldownUntil <= 0) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const remaining = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const onCooldown = remaining > 0;

  const handleResend = async () => {
    if (busy || onCooldown) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/lifetime/verification/resend`, { email });
      toast.success('Verification email sent. Please check your inbox.');
      setCooldownUntil(Date.now() + 60_000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || 'Please wait a moment before trying again.');
        // Try to parse `wait` seconds from the detail (best-effort UX).
        const match = String(detail || '').match(/(\d+)\s*seconds?/);
        const secs = match ? parseInt(match[1], 10) : 60;
        setCooldownUntil(Date.now() + secs * 1000);
      } else {
        toast.error(detail || 'Could not resend verification email.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-br from-slate-50 to-orange-50/40"
      data-testid="lifetime-redeem-pending"
    >
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mx-auto mb-4">
            <MailCheck size={24} />
          </div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Check your inbox
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            We sent a verification link to <span className="font-medium text-slate-900" data-testid="lt-pending-email">{email}</span>.
            Click it within <span className="font-medium">24 hours</span> to activate your Lifetime license.
          </p>
          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-3 text-left text-xs text-slate-600 space-y-1">
            <p>• The link is valid for 24 hours.</p>
            <p>• If you don’t verify in time, the license code will be released so you can re-redeem.</p>
            <p>• Didn’t get it? Check spam, or use the button below to resend.</p>
          </div>
          <Button
            type="button"
            onClick={handleResend}
            disabled={busy || onCooldown}
            className="w-full mt-5 bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="lt-pending-resend"
          >
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
            {onCooldown
              ? `Resend available in ${remaining}s`
              : 'Resend verification email'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="w-full mt-2"
            data-testid="lt-pending-login-link"
          >
            Back to sign in
          </Button>
        </div>
      </div>
    </div>
  );
}
